import React from 'react';
import useRect from '@smart-tv/hooks/use-rect';

export interface SliderContextProps {
  ref: React.RefObject<HTMLDivElement>;
  selectedSlide: number;
  slideCount: number;
  setSlideCount: React.Dispatch<React.SetStateAction<number>>;
  offset: number;
  next: () => void;
  nextDisabled: boolean;
  prev: () => void;
  prevDisabled: boolean;
}

export const SliderContext = React.createContext({} as SliderContextProps);

interface SliderConsumerProps {
  children: React.ReactNode | React.ReactNode[];
}

export const SliderConsumer = ({ children }: SliderConsumerProps): JSX.Element => {
  const { offset, ref, slideCount, setSlideCount } = React.useContext(SliderContext);

  React.useEffect(() => {
    if (React.Children.count(children) !== slideCount) {
      setSlideCount(React.Children.count(children));
    }
  }, [children, slideCount, setSlideCount]);

  return <>{children}</>;
};

interface SliderProviderProps {
  children: React.ReactNode | React.ReactNode[];
}

export const SliderProvider = ({ children }: SliderProviderProps): JSX.Element => {
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const sliderRect = useRect(sliderRef);

  const [offset, setOffset] = React.useState(0);
  const [slideSizes, setSlideSizes] = React.useState<number[]>();
  const [slideCount, setSlideCount] = React.useState(0);
  const [nextDisabled, setNextDisabled] = React.useState(false);
  const [prevDisabled, setPrevDisabled] = React.useState(true);
  const [selectedSlide, setSelectedSlide] = React.useState(0);

  const nextSlide = React.useCallback(
    (currentSlide: number) => {
      if (sliderRect?.width && slideSizes?.length) {
        let nextSlide = currentSlide;
        let pageWidth = slideSizes[nextSlide];

        while (pageWidth <= sliderRect.width) {
          pageWidth += slideSizes[nextSlide + 1];
          nextSlide++;
        }

        return nextSlide;
      }

      return 0;
    },
    [sliderRect, slideSizes]
  );

  const prevSlide = React.useCallback(
    (currentSlide: number) => {
      if (sliderRect?.width && slideSizes?.length) {
        let prevSlide = currentSlide;
        let pageWidth = slideSizes[prevSlide];

        while (pageWidth <= sliderRect.width) {
          pageWidth += slideSizes[prevSlide - 1];
          prevSlide--;
        }

        return prevSlide >= 0 ? prevSlide : 0;
      }

      return 0;
    },
    [sliderRect, slideSizes]
  );

  const setSliderButtonStates = React.useCallback(
    (targetSlide: number) => {
      setNextDisabled(nextSlide(targetSlide) > slideCount - 1);
      setPrevDisabled(targetSlide <= 0);
    },
    [nextSlide, setNextDisabled, setPrevDisabled, slideCount]
  );

  const next = React.useCallback(() => {
    const targetSlide = nextSlide(selectedSlide);

    if (targetSlide <= slideCount - 1) {
      setSelectedSlide(targetSlide);
    }
  }, [setSelectedSlide, nextSlide, slideCount, selectedSlide]);

  const prev = React.useCallback(() => {
    const targetSlide = prevSlide(selectedSlide);

    if (targetSlide >= 0) {
      setSelectedSlide(targetSlide);
    }
  }, [setSelectedSlide, prevSlide, selectedSlide]);

  // set slider offset
  React.useEffect(() => {
    if (slideSizes?.length && selectedSlide > 0) {
      const accumulatedSize = slideSizes.slice(0, selectedSlide)?.reduce((a, b) => a + b);
      setOffset(accumulatedSize);
    } else {
      setOffset(0);
    }
  }, [selectedSlide, slideSizes]);

  React.useEffect(() => {
    if (sliderRef.current) {
      const sizes = Array.from(sliderRef.current.children).map(
        (child) => child.getBoundingClientRect().width
      );
      setSlideSizes(sizes);
    }
  }, [sliderRef, sliderRect, slideCount, setSlideSizes]);

  // if users removes items from slider and so the selected slide
  React.useEffect(() => {
    if (selectedSlide > slideCount - 1) {
      prev();
    }
  }, [slideCount, selectedSlide, prev]);

  React.useEffect(() => {
    setSliderButtonStates(selectedSlide);
  }, [setSliderButtonStates, selectedSlide, sliderRect]);

  const sliderValues = {
    selectedSlide,
    slideCount,
    setSlideCount,
    offset,
    next,
    nextDisabled,
    prev,
    prevDisabled,
    ref: sliderRef
  };

  return <SliderContext.Provider value={sliderValues}>{children}</SliderContext.Provider>;
};

export default SliderProvider;
